import React from 'react';

/**
 * Reveal — scroll-entrance wrapper. Transform-only rise (translateY 16 → 0)
 * over 0.7s with the brand easing. Opacity stays at 1 so throttled/SSR/
 * reduced-motion contexts never show blank content. Honors prefers-reduced-motion.
 */
export function Reveal({ children, delay = 0, as: Tag = 'div', style = {}, ...rest }) {
  const ref = React.useRef(null);
  const reduce = typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const [shown, setShown] = React.useState(reduce);

  React.useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setShown(true);
      cleanup();
    };
    const check = () => {
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.94 && r.bottom > 0) reveal();
    };
    const cleanup = () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      clearTimeout(safety);
    };
    const safety = setTimeout(reveal, 1600);
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const raf = requestAnimationFrame(check);
    return () => {
      cleanup();
      cancelAnimationFrame(raf);
    };
  }, [reduce]);

  const motion = reduce
    ? {}
    : {
        transform: shown ? 'none' : 'translateY(16px)',
        transition: `transform 0.7s var(--ease) ${delay}ms`,
        willChange: 'transform',
      };

  return (
    <Tag ref={ref} style={{ ...motion, ...style }} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * Eyebrow — small uppercase tan kicker with a short 22px rule.
 * Luminous variant only (no index number).
 */
export function Eyebrow({ children, onDark = false, center = false }) {
  const color = onDark ? 'var(--tan-200)' : 'var(--tan-700)';
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '14px',
      fontFamily: 'var(--font-ui)',
      fontSize: '12.5px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.2em',
      color,
      justifyContent: center ? 'center' : 'flex-start',
    }}>
      <span style={{ width: '22px', height: '1.5px', background: 'currentColor', opacity: 0.55 }} />
      <span>{children}</span>
    </div>
  );
}

/**
 * SectionTitle — eyebrow + airy serif h2 + optional subtitle.
 * Title color defaults to navy-800 (Luminous), or white on dark sections.
 */
export function SectionTitle({ eyebrow, title, subtitle, onDark = false, align = 'center', maxw = '640px' }) {
  const titleColor = onDark ? '#fff' : 'var(--navy-800)';
  const subColor = onDark ? 'rgba(255,255,255,0.82)' : 'var(--text-body)';
  const isCenter = align === 'center';
  return (
    <div style={{ textAlign: align, maxWidth: isCenter ? maxw : 'none', margin: isCenter ? '0 auto' : 0 }}>
      {eyebrow && (
        <div style={{ marginBottom: '20px' }}>
          <Eyebrow onDark={onDark} center={isCenter}>{eyebrow}</Eyebrow>
        </div>
      )}
      <h2 className="v2-serif" style={{
        fontSize: 'clamp(34px, 4.4vw, 54px)',
        lineHeight: 1.08,
        color: titleColor,
        margin: 0,
        fontWeight: 500,
      }}>{title}</h2>
      {subtitle && (
        <p style={{
          fontFamily: 'var(--font-ui)',
          fontSize: '18px',
          lineHeight: 1.7,
          color: subColor,
          margin: '20px auto 0',
          maxWidth: isCenter ? '560px' : '620px',
          marginLeft: isCenter ? 'auto' : 0,
          marginRight: isCenter ? 'auto' : 0,
        }}>{subtitle}</p>
      )}
    </div>
  );
}

/**
 * PageHero — inner-page hero: gradient-hero wash over a center/cover photo,
 * centered white serif title + tan subtitle, min-height 38vh.
 */
export function PageHero({ eyebrow, title, subtitle, image }) {
  return (
    <section style={{
      position: 'relative',
      minHeight: '38vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      color: '#fff',
      background: image
        ? `var(--gradient-hero), center/cover no-repeat url(${image})`
        : 'var(--gradient-hero)',
    }}>
      <div style={{ padding: '40px' }}>
        {eyebrow && (
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            color: 'var(--tan-200)',
            marginBottom: '14px',
          }}>{eyebrow}</div>
        )}
        <h1 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(40px, 6vw, 60px)',
          fontWeight: 500,
          margin: '0 0 12px',
          letterSpacing: '-0.02em',
        }}>{title}</h1>
        <p style={{ fontSize: '20px', color: 'var(--tan-200)', margin: 0 }}>{subtitle}</p>
      </div>
    </section>
  );
}
